/* eslint-disable custom-rules/zod-enforce-nullish */
import { z } from 'zod'

import { AddressSource } from '@forto-dev/customer-master-data-models'
import { isSanctionedAddress, isSanctionedCountry } from '@freighthub/reference-master-data'

export const isLocalLanguageCountryRequired = (localAddress: AddressData) => {
  return !!(localAddress.region1 || localAddress.locality1 || localAddress.postalCode || localAddress.route1)
}

const isAddressSanctioned = (address: AddressData): boolean => {
  if (!address.countryCode) {
    return false
  }

  return isSanctionedAddress({
    countryCode: address.countryCode,
    route1: address.route1,
    route2: address.route2,
    route3: address.route3,
    route4: address.route4,
    locality1: address.locality1,
    locality2: address.locality2,
    locality3: address.locality3,
    locality4: address.locality4,
    region1: address.region1,
    region2: address.region2,
    region3: address.region3,
    region4: address.region4,
  })
}

export const addressDataSchema = z.object({
  companyName: z.string().optional(),
  route1: z.string().optional(),
  route2: z.string().optional(),
  route3: z.string().optional(),
  route4: z.string().optional(),
  additionalInformation: z.string().optional(),
  locality1: z.string().optional(),
  locality2: z.string().optional(),
  locality3: z.string().optional(),
  locality4: z.string().optional(),
  region1: z.string().optional(),
  region2: z.string().optional(),
  region3: z.string().optional(),
  region4: z.string().optional(),
  postalCode: z.string().optional(),
  countryCode: z.string().optional(),
  locationRef: z.string().optional(),
  source: z.enum([AddressSource.AUTOCOMPLETE, AddressSource.MANUAL]).optional(),
})

export const addressFormDataSchema = z
  .object({
    address: addressDataSchema.superRefine((request, ctx) => {
      // if manual address
      if (request.source === AddressSource.MANUAL) {
        if (!request.countryCode) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['countryCode'],
            message: 'locations.search_address_form.country_required_error',
          })
        }
        if (!request.route1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['route1'],
            message: 'locations.search_address_form.street_name_required_error',
          })
        }
      }

      if (request.countryCode && isSanctionedCountry(request.countryCode)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['countryCode'],
          message:
            request.source == AddressSource.MANUAL
              ? 'locations.search_address_form.country_sanctioned_error'
              : 'locations.search_address_sanctioned_error',
        })
      }
    }),
    addressFormatted: z.string().nullable(),
    localAddress: addressDataSchema.nullable(),
    localAddressFormatted: z.string().nullable(),
  })
  .superRefine((request, ctx) => {
    if (request.address.source === AddressSource.MANUAL && !!request.localAddress) {
      if (
        request.address.countryCode &&
        request.localAddress.countryCode &&
        request.address.countryCode !== request.localAddress.countryCode
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['localAddress', 'countryCode'],
          message: 'locations.search_address_form.local_country_mismatch_error',
        })
      }

      if (!request.localAddress.countryCode && isLocalLanguageCountryRequired(request.localAddress)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['localAddress', 'countryCode'],
          message: 'locations.search_address_form.local_country_required_error',
        })
      }
    }

    if (request.address && isAddressSanctioned(request.address)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `locations.search_address_sanctioned_error`,
        path: ['address', 'shared'],
      })
    }

    if (request.localAddress && isAddressSanctioned(request.localAddress)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `locations.search_address_form.local_address_sanctioned_error`,
        path: ['localAddress', 'shared'],
      })
    }
  })

export type AddressData = z.infer<typeof addressDataSchema>

export type AddressFormData = z.infer<typeof addressFormDataSchema>
